import { ChevronLeft, ChevronRight, Cloud, ErrorOutlineOutlined, InfoOutlined, WbSunny } from '@mui/icons-material';
import AcUnit from '@mui/icons-material/AcUnit';
import Air from '@mui/icons-material/Air';
import LightMode from '@mui/icons-material/LightMode';
import Opacity from '@mui/icons-material/Opacity';
import WaterDrop from '@mui/icons-material/WaterDrop';
import { Box, IconButton, Link, Skeleton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '/@/components/bayer/alert';
import ModalV2 from '/@/components/bayer/modal-v2';
import ChipLabel from '/@/components/chip-label';
import GDUGraphic from '/@/components/crop-monitoring/gdu-graphic';
import GraphicCards from '/@/components/crop-monitoring/graphic/graphic-cards';
import { BASE_STEPS, getActiveStep, getLabelStep } from '/@/components/crop-monitoring/progress-bar/assets/BASE_STEPS';
import { getWeatherCodeData } from '/@/components/field-list-v2/components/sidebar/components/weather-mini-card/weather-codes';
import Icon, { type IconType } from '/@/components/icon';
import colors from '/@/components/theme-provider/colors';
import WeatherStatusModal from '/@/components/weather-status-modal';
import useUnitProvider from '/@/core/context/unit';
import settings from '/@/core/settings';
import { useFieldById } from '/@/hooks/fields';
import { useWeatherForecast } from '/@/hooks/frost-risk';
import { usePhenologyForProducts } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { HistoricalWeather } from '/@/types/weather/forecast';
import { formatDate } from '/@/utils';
import { reverse } from '/@/utils/router';
import { getSuggestion } from '/@/views/fields/field-detail/field-weather/application-risk';
import ground from './assets/fallow.svg';

const EmptyState = ({ type }: { type: IconType }) => {
    return (
        <Stack
            spacing={1.5}
            justifyContent='center'
            alignItems='center'
            sx={{ textAlign: 'center', backgroundColor: 'grey.100', p: 3, borderRadius: 1, minHeight: 220 }}
        >
            <Icon type={type} size='xxl' />
            <Typography color='primary' variant='subtitle1' fontWeight='bold'>
                {_('Fenología no disponible para este cultivo')}
            </Typography>
            <Typography color='primary' variant='body2'>
                {_('Por ahora no contamos con el modelo de datos para mostrar la fenología de este cultivo.')}
            </Typography>
        </Stack>
    );
};

const BARBECHO_STEP = {
    displayName: 'Barbecho',
    stage: 'barbecho',
    phenology: 'barbecho',
    image: ground,
} as const;

const ALL_NAV_STEPS = [BARBECHO_STEP, ...BASE_STEPS.filter((s) => !s.empty && Boolean(s.image))];

const FieldCurrentStatus = ({
    cropMonitoring,
    technology,
    showCropProtection,
    showAlert,
    historicalWeather,
}: {
    cropMonitoring?: CropMonitoring;
    technology: string;
    showCropProtection: boolean;
    showAlert: boolean;
    historicalWeather: HistoricalWeather;
}) => {
    const [gduDialogOpen, setGduDialogOpen] = useState(false);
    const [weatherDialogOpen, setWeatherDialogOpen] = useState(false);
    const [weatherModalOpen, setWeatherModalOpen] = useState(false);
    const navigate = useNavigate();
    const units = useUnitProvider();

    // Same shape as the tabbed "Datos meteorológicos" card in `cards-container`, reused here
    // inside a modal instead of inline.
    const weatherCardsConfiguration = useMemo(
        () => ({
            temperature: {
                title: _('Temperatura  ({{temperature}} / Días)', {
                    temperature: units.getUnit('temperature', 'corn'),
                }),
                quantity: 10,
                icon: <LightMode sx={{ fontSize: 24 }} />,
                showYAxis: true,
                showXAxis: true,
                domain: [units.toTemperature(-10, 0).value, units.toTemperature(50, 0).value],
                graphicType: 'line' as const,
                max: units.toTemperature(35, 0).value,
                min: units.toTemperature(3, 0).value,
                references: [
                    { label: _('Riesgo de stress térmico'), color: 'red', dashed: true },
                    { label: _('Riesgo de helada'), color: 'cornflowerblue', dashed: true },
                    { label: _('Temperatura máxima'), color: 'red' },
                    { label: _('Temperatura mínima'), color: 'cornflowerblue' },
                ],
            },
            rain: {
                title: _('Precipitaciones  ({{rain}} / Días)', { rain: units.getUnit('rain', 'corn') }),
                quantity: 10,
                icon: <WaterDrop sx={{ fontSize: 24 }} />,
                showYAxis: true,
                showXAxis: true,
                domain: [0, units.toRain(100, 0).value],
                references: [
                    { label: _('Precipitaciones acumuladas'), color: '#7AB3FF' },
                    { label: _('Precipitaciones diarias'), color: colors.themes.cultivio.primary.default },
                ],
            },
            wind: {
                title: _('Viento  ({{speed}} / Días)', { speed: units.getUnit('speed', 'corn') }),
                quantity: 10,
                icon: <Air sx={{ fontSize: 24 }} />,
                showYAxis: true,
                showXAxis: true,
                domain: [0, units.toSpeed(60, 0).value],
                max: units.toSpeed(39, 0)?.value,
                graphicType: 'line' as const,
                references: [{ label: _('Vientos fuertes'), color: 'red' }],
            },
            humidity: {
                title: _('Humedad  (% / Días)'),
                quantity: 10,
                icon: <Opacity sx={{ fontSize: 24 }} />,
                showYAxis: true,
                showXAxis: true,
                domain: [0, 100],
                graphicType: 'line' as const,
                max: null,
                references: [{ label: _('Humedad'), color: colors.themes.cultivio.primary.default }],
            },
        }),
        [units]
    );

    // The most recent day's `precipAccum` is already a running total across the whole
    // fetched range (the API accumulates it server-side), so it doubles as "total acumuladas".
    const latestWeatherEntry = useMemo(() => {
        const results = historicalWeather?.results;
        if (!results?.length) return undefined;
        return results.reduce((latest, entry) => (entry.date > latest.date ? entry : latest));
    }, [historicalWeather]);
    const totalPrecipitation = latestWeatherEntry ? units.toRain(latestWeatherEntry.precipAccum, 0).toString() : null;

    const averageTemperatureLast7Days = useMemo(() => {
        const results = historicalWeather?.results;
        if (!results?.length) return undefined;
        const last7Days = [...results].sort((a, b) => (a.date > b.date ? -1 : 1)).slice(0, 7);
        const avg = last7Days.reduce((sum, entry) => sum + (entry.tempMax + entry.tempMin) / 2, 0) / last7Days.length;
        return units.toTemperature(avg, 0).toString();
    }, [historicalWeather, units]);

    const lastStage = cropMonitoring?.cornCropMonitoring?.stages?.at(-1);
    const accumulatedGdu = cropMonitoring?.cornCropMonitoring?.accumulatedGdu;
    const activeStep = lastStage
        ? getActiveStep({
              vegetativeStage: lastStage.vegetativeStage ?? undefined,
              phenology: lastStage.phenology ?? undefined,
          })
        : undefined;

    const activeNavIndex = useMemo(
        () =>
            activeStep
                ? Math.max(
                      ALL_NAV_STEPS.findIndex((s) => s === activeStep),
                      0
                  )
                : 0, // No active crop monitoring → barbecho
        [activeStep]
    );
    const [displayedNavIndex, setDisplayedNavIndex] = useState(activeNavIndex);
    useEffect(() => {
        setDisplayedNavIndex(activeNavIndex);
    }, [activeNavIndex]);
    const displayedStep = ALL_NAV_STEPS[displayedNavIndex];

    const isBarbecho = displayedStep === BARBECHO_STEP;
    const currentStageCode = isBarbecho ? _('Barbecho') : (displayedStep?.displayName ?? undefined);
    const currentStageLabel = isBarbecho
        ? null
        : getLabelStep(displayedStep?.vegetativeStage, displayedStep?.phenology);
    const expectedFloweringDate = cropMonitoring?.cornCropMonitoring?.expectedFloweringDate;
    const today = dayjs().startOf('day');
    const criticalPeriodDate = expectedFloweringDate ? dayjs(expectedFloweringDate).startOf('day') : null;
    const criticalPeriodStart = criticalPeriodDate?.subtract(15, 'day') ?? null;
    const criticalPeriodEnd = criticalPeriodDate?.add(15, 'day') ?? null;
    const fieldId = cropMonitoring?.field;
    const fieldQuery = useFieldById({ id: fieldId ?? 0 }, { enabled: !!fieldId });
    const productId = cropMonitoring?.cornCropMonitoring?.product;
    const phenologyQuery = usePhenologyForProducts(
        {
            crop: 'corn',
            lat: fieldQuery.data?.lat,
            lng: fieldQuery.data?.lng,
            productsId: productId ? [productId] : undefined,
            plantingDate: cropMonitoring?.plantingDate,
        },
        {
            enabled: Boolean(
                cropMonitoring?.crop === 'corn' &&
                    productId &&
                    fieldQuery.data?.lat &&
                    fieldQuery.data?.lng &&
                    cropMonitoring?.plantingDate
            ),
        }
    );
    const gdusToMaturity = phenologyQuery?.data?.[productId ?? 0]?.gdusTo?.maturity ?? undefined;

    // "Estado del tiempo" — today's conditions, spray-suitability, and 7-day frost risk, all
    // sourced from the same forecast payload (mirrors WeatherForecastMiniCard's extraction).
    const weatherForecastQuery = useWeatherForecast({
        lat: fieldQuery.data?.lat ?? 0,
        lng: fieldQuery.data?.lng ?? 0,
    });
    const weatherForecastData = weatherForecastQuery.data?.data;
    const currentForecast = weatherForecastData?.current.data;
    const weatherCodeData = useMemo(
        () => getWeatherCodeData(currentForecast?.currentIcon),
        [currentForecast?.currentIcon]
    );
    const currentTempDisplay = currentForecast?.currentTemp
        ? units.toTemperature(currentForecast.currentTemp, 0).toString()
        : null;
    const { minTempDisplay, maxTempDisplay } = useMemo(() => {
        const todayISO = new Date().toISOString().split('T')[0];
        const forecast = weatherForecastData?.forecast.find((f) => f.date === todayISO)?.data?.[0];
        if (!forecast) return { minTempDisplay: null, maxTempDisplay: null };
        const min = Math.min(forecast.day.minTemp, forecast.night.minTemp);
        const max = Math.max(forecast.day.maxTemp, forecast.night.maxTemp);
        return {
            minTempDisplay: units.toTemperature(min, 0).toString(),
            maxTempDisplay: units.toTemperature(max, 0).toString(),
        };
    }, [weatherForecastData?.forecast, units]);
    const applicationSuggestion = currentForecast?.applicationRisk
        ? getSuggestion(currentForecast.applicationRisk)
        : undefined;
    const hasFrostRiskNext7Days = weatherForecastData?.lowGDUsRisk?.['7d']
        ? weatherForecastData.lowGDUsRisk['7d'] !== 'G'
        : false;

    const isBeforeCriticalPeriod = criticalPeriodStart ? today.isBefore(criticalPeriodStart) : false;
    const isAfterCriticalPeriod = criticalPeriodEnd ? today.isAfter(criticalPeriodEnd) : false;
    const isCriticalPeriod = Boolean(criticalPeriodDate && !isBeforeCriticalPeriod && !isAfterCriticalPeriod);

    const stageDate = useMemo(() => {
        if (isBarbecho) return _('Período de descanso');
        const match = cropMonitoring?.cornCropMonitoring?.stages?.find(
            (s) =>
                (displayedStep?.vegetativeStage !== undefined && s.vegetativeStage === displayedStep.vegetativeStage) ||
                (displayedStep?.phenology !== undefined && s.phenology === displayedStep.phenology)
        );
        const raw = match?.date ?? cropMonitoring?.plantingDate;
        return raw ? formatDate(new Date(raw)) : null;
    }, [isBarbecho, displayedStep, cropMonitoring]);

    const cornCarousel = (
        <Box>
            <Stack direction='row' alignItems='center' spacing={0.5}>
                {displayedNavIndex > 0 && (
                    <IconButton size='small' onClick={() => setDisplayedNavIndex((i) => i - 1)}>
                        <ChevronLeft />
                    </IconButton>
                )}
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minHeight: 220 }}>
                    {!isBarbecho && (
                        <Box
                            component='img'
                            src={displayedStep?.image}
                            alt={displayedStep?.displayName}
                            sx={{ maxHeight: 220, objectFit: 'contain' }}
                        />
                    )}
                </Box>
                <IconButton
                    size='small'
                    disabled={displayedNavIndex >= activeNavIndex}
                    onClick={() => setDisplayedNavIndex((i) => i + 1)}
                    sx={{ opacity: displayedNavIndex >= activeNavIndex ? 0 : 1 }}
                >
                    <ChevronRight />
                </IconButton>
            </Stack>
            {isBarbecho ? (
                <Box
                    component='img'
                    src={ground}
                    alt='barbecho'
                    sx={{ width: '100%', height: 15, objectFit: 'cover', objectPosition: 'top' }}
                />
            ) : (
                <Box
                    sx={{
                        height: 10,
                        background: 'linear-gradient(to bottom, #a8d455, #7cb828)',
                        borderRadius: '2px',
                    }}
                />
            )}
        </Box>
    );

    const graphic = cropMonitoring?.crop
        ? {
              corn: cornCarousel,
              soy: <EmptyState type='soy2' />,
              wheat: <EmptyState type='wheat' />,
              barley: <EmptyState type='barley' />,
          }[cropMonitoring.crop]
        : cornCarousel; // No crop monitoring → show barbecho carousel

    const isCorn = cropMonitoring?.crop === 'corn';
    const showCarouselInfo = isCorn || !cropMonitoring?.crop;

    return (
        <Stack spacing={2} sx={{ backgroundColor: 'grey.100', p: 2 }}>
            <Typography variant='h5' color='primary'>
                {_('Estado actual del lote')}
            </Typography>
            <Stack direction='column' spacing={2} sx={{ background: 'white', p: 2, border: '1px solid divider' }}>
                <Typography variant='h6' color='primary'>
                    {_('Fenología')}
                </Typography>
                {graphic}
                {showCarouselInfo && (currentStageCode || currentStageLabel || stageDate) && (
                    <Stack direction='column' spacing={2}>
                        <Stack direction='row' justifyContent='space-between' alignItems='flex-start' mt={1}>
                            <Stack spacing={0.25}>
                                <Typography variant='h6' color='primary'>
                                    {currentStageCode ?? currentStageLabel}
                                </Typography>
                                {stageDate && (
                                    <Typography variant='caption' color='primary.mui.inactive'>
                                        {stageDate}
                                    </Typography>
                                )}
                            </Stack>

                            {currentStageLabel && (
                                <Stack spacing={0.25} alignItems='flex-end'>
                                    <Typography variant='subtitle1' color='primary'>
                                        {currentStageLabel}
                                    </Typography>
                                    {isCriticalPeriod && (
                                        <Stack direction='row' spacing={0.5} alignItems='center'>
                                            <ErrorOutlineOutlined sx={{ fontSize: 14, color: 'warning.main' }} />
                                            <Typography variant='caption' color='warning.main'>
                                                {_('Período crítico')}
                                            </Typography>
                                        </Stack>
                                    )}
                                    {isBeforeCriticalPeriod && (
                                        <Stack direction='row' alignItems='flex-start' spacing={0.5}>
                                            <InfoOutlined
                                                sx={{ fontSize: 14, color: 'primary.mui.inactive', mt: '2px' }}
                                            />
                                            <Typography variant='caption' color='primary.mui.inactive'>
                                                {`PC ${formatDate(new Date(expectedFloweringDate ?? ''))}`}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                        {(lastStage?.vegetativeStage || 0) >= 11 && gdusToMaturity && (
                            <Alert severity='surfaceVariant'>
                                {_(
                                    'El modelo de fenología de maíz cubre hasta R1 y salta a MF después de {{gdus}} GDUs relativos.',
                                    { gdus: gdusToMaturity?.toFixed(0) ?? 0 }
                                )}
                            </Alert>
                        )}
                    </Stack>
                )}

                <Link
                    component='button'
                    underline='always'
                    variant='body2'
                    onClick={() => navigate(reverse('productExploration.cropProtection.whenToApply'))}
                    sx={{ mt: 1, display: 'block', cursor: 'pointer', textAlign: 'left' }}
                >
                    {_('Cuándo aplicar fitosanitarios')}
                </Link>
            </Stack>

            {/* GDUs acumulados — corn only */}
            {isCorn && accumulatedGdu !== undefined && (
                <>
                    <Stack
                        direction='column'
                        spacing={2}
                        sx={{ background: 'white', p: 2, border: '1px solid divider' }}
                    >
                        <Typography variant='h6' color='primary'>
                            {_('GDUs acumulados')}
                        </Typography>
                        <Typography variant='h4' color='primary'>
                            {accumulatedGdu}
                        </Typography>
                        <Link
                            component='button'
                            underline='always'
                            variant='body2'
                            onClick={() => setGduDialogOpen(true)}
                            sx={{ cursor: 'pointer', textAlign: 'left' }}
                        >
                            {_('Ver evolución diaria')}
                        </Link>
                    </Stack>

                    {weatherModalOpen && fieldQuery.data?.lat && fieldQuery.data?.lng && (
                        <WeatherStatusModal
                            open={weatherModalOpen}
                            onClose={() => setWeatherModalOpen(false)}
                            lat={fieldQuery.data.lat}
                            lng={fieldQuery.data.lng}
                            locationName={fieldQuery.data.location}
                            onOpenFrostMap={() => {
                                navigate(reverse('frost_risk_map'));
                            }}
                        />
                    )}

                    <ModalV2
                        open={gduDialogOpen}
                        onClose={() => setGduDialogOpen(false)}
                        title={_('Avance de GDUs y estadio vegetativo')}
                        maxWidth='md'
                    >
                        {historicalWeather && cropMonitoring && (
                            <>
                                <GDUGraphic
                                    cropMonitoring={cropMonitoring}
                                    historicalWeather={historicalWeather}
                                    isPdf={false}
                                />
                                <Stack direction='row' spacing={0.5} justifyContent='flex-start' width='100%'>
                                    <Typography variant='body2' color='primary.mui.inactive'>
                                        {_('Referencias')}:
                                    </Typography>
                                    <Stack direction='row' spacing={0.5} alignItems='center'>
                                        <Box
                                            sx={{
                                                width: 5,
                                                height: 5,
                                                backgroundColor: '#00BCFF',
                                                borderRadius: '50%',
                                            }}
                                        />
                                        <Typography variant='body2' color='primary.mui.inactive'>
                                            {_('GDU')}:
                                        </Typography>
                                    </Stack>
                                    <Stack direction='row' spacing={0.5} alignItems='center'>
                                        <Box
                                            sx={{
                                                width: 5,
                                                height: 5,
                                                backgroundColor: '#89D329',
                                                borderRadius: '50%',
                                            }}
                                        />
                                        <Typography variant='body2' color='primary.mui.inactive'>
                                            {_('Estado Vegetativo')}:
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </>
                        )}
                    </ModalV2>
                </>
            )}

            {/* Estado del tiempo */}
            {!!(fieldQuery.data?.lat && fieldQuery.data?.lng) && (
                <Stack direction='column' spacing={2} sx={{ background: 'white', p: 2, border: '1px solid divider' }}>
                    <Typography variant='h6' color='primary'>
                        {_('Estado del tiempo')}
                    </Typography>
                    <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={0.5}>
                        <Stack>
                            <Typography variant='subtitle2' fontWeight='bold' color='primary'>
                                {_('Hoy')}
                            </Typography>
                            <Typography variant='body2' color='primary'>
                                {weatherCodeData?.description ? (
                                    _(weatherCodeData.description)
                                ) : (
                                    <Skeleton variant='text' width={80} />
                                )}
                            </Typography>
                        </Stack>
                        <Stack direction='row' alignItems='center' spacing={0.5}>
                            <Typography variant='h4' color='primary'>
                                {currentTempDisplay ?? <Skeleton variant='text' width={60} />}
                            </Typography>
                            {weatherCodeData?.image && (
                                <Box
                                    component='img'
                                    src={weatherCodeData.image}
                                    alt={weatherCodeData.description ? _(weatherCodeData.description) : ''}
                                    width={24}
                                    height={24}
                                />
                            )}
                        </Stack>
                        <Typography variant='body2' color='primary'>
                            {minTempDisplay && maxTempDisplay ? (
                                `${_('Min')} ${minTempDisplay} ${_('Max')} ${maxTempDisplay}`
                            ) : (
                                <Skeleton variant='text' width={100} />
                            )}
                        </Typography>
                    </Stack>
                    {applicationSuggestion?.color === 'G' && (
                        <ChipLabel
                            type='success'
                            label={_('Apto para aplicar')}
                            icon={<InfoOutlined sx={{ fontSize: 14 }} />}
                        />
                    )}
                    {hasFrostRiskNext7Days && (
                        <Alert severity='danger' icon={<AcUnit sx={{ fontSize: 22 }} />}>
                            {_('Riesgo de heladas en los próx. 7 días')}
                        </Alert>
                    )}
                    <Link
                        component='button'
                        underline='always'
                        variant='body2'
                        onClick={() => setWeatherModalOpen(true)}
                        sx={{ cursor: 'pointer', textAlign: 'left' }}
                    >
                        {_('Ver pronostico extendido')}
                    </Link>
                </Stack>
            )}

            {/* Datos meteorológicos */}
            {!!historicalWeather?.results?.length && (
                <>
                    <Stack
                        direction='column'
                        spacing={2}
                        sx={{ background: 'white', p: 2, border: '1px solid divider' }}
                    >
                        <Typography variant='h6' color='primary'>
                            {_('Datos meteorológicos')}
                        </Typography>
                        <Stack spacing={1}>
                            <Stack
                                direction='column'
                                spacing={1}
                                sx={{
                                    backgroundColor: colors.themes[settings.app.theme].mui.primary.infoBackground,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    p: 1.5,
                                }}
                            >
                                <Stack direction='row' spacing={1} alignItems='center'>
                                    <Cloud sx={{ fontSize: 20, color: 'primary.main' }} />
                                    <Typography variant='subtitle2' fontWeight='bold' color='primary'>
                                        {_('Precipitaciones')}
                                    </Typography>
                                </Stack>
                                <Typography variant='h6' color='primary'>
                                    {totalPrecipitation ?? '—'}
                                </Typography>
                                <Typography variant='caption' color='primary.mui.inactive'>
                                    {_('Total acumuladas')}
                                </Typography>
                            </Stack>
                            <Stack
                                direction='column'
                                spacing={1}
                                sx={{
                                    backgroundColor: colors.themes[settings.app.theme].mui.primary.infoBackground,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    p: 1.5,
                                }}
                            >
                                <Stack direction='row' spacing={1} alignItems='center'>
                                    <WbSunny sx={{ fontSize: 20, color: 'primary.main' }} />
                                    <Typography variant='subtitle2' fontWeight='bold' color='primary'>
                                        {_('Temperatura media')}
                                    </Typography>
                                </Stack>
                                <Typography variant='h6' color='primary'>
                                    {averageTemperatureLast7Days ?? '—'}
                                </Typography>
                                <Typography variant='caption' color='primary.mui.inactive'>
                                    {_('Últimos 7 días')}
                                </Typography>
                            </Stack>
                        </Stack>
                        <Link
                            component='button'
                            underline='always'
                            variant='body2'
                            onClick={() => setWeatherDialogOpen(true)}
                            sx={{ cursor: 'pointer', textAlign: 'left' }}
                        >
                            {_('Ver datos históricos')}
                        </Link>
                    </Stack>

                    <ModalV2
                        open={weatherDialogOpen}
                        onClose={() => setWeatherDialogOpen(false)}
                        maxWidth='md'
                        title={_('Datos meteorológicos')}
                    >
                        <GraphicCards
                            weatherCardsConfiguration={weatherCardsConfiguration}
                            historicalWeather={historicalWeather}
                            hideTitle={true}
                            hideBorders={true}
                        />
                    </ModalV2>
                </>
            )}
        </Stack>
    );
};

export default FieldCurrentStatus;
